import { useCallback, useEffect, useRef } from "react";

/**
 * Synthesizes a soft wind/rustle whoosh using the Web Audio API.
 * No external audio file — generated from filtered noise with a gentle
 * attack/decay envelope so it lands organically during a visual transition.
 */
export function useWindRustle() {
  const ctxRef = useRef<AudioContext | null>(null);

  // Lazily create / resume the AudioContext on first use (browser policy).
  const getCtx = useCallback((): AudioContext | null => {
    if (typeof window === "undefined") return null;
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) return null;
    if (!ctxRef.current) {
      ctxRef.current = new AC();
    }
    if (ctxRef.current.state === "suspended") {
      void ctxRef.current.resume();
    }
    return ctxRef.current;
  }, []);

  const play = useCallback(
    (durationSec = 1.4) => {
      const ctx = getCtx();
      if (!ctx) return;

      const now = ctx.currentTime;
      const dur = Math.max(0.2, durationSec);

      // 1) Generate a buffer of pink-ish noise
      const sampleRate = ctx.sampleRate;
      const bufferLen = Math.floor(sampleRate * dur);
      const buffer = ctx.createBuffer(1, bufferLen, sampleRate);
      const data = buffer.getChannelData(0);

      // Paul Kellet pink noise approximation
      let b0 = 0;
      let b1 = 0;
      let b2 = 0;
      let b3 = 0;
      let b4 = 0;
      let b5 = 0;
      let b6 = 0;
      for (let i = 0; i < bufferLen; i += 1) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.969 * b2 + white * 0.153852;
        b3 = 0.8665 * b3 + white * 0.3104856;
        b4 = 0.55 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.016898;
        const pink = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
        b6 = white * 0.115926;
        data[i] = pink * 0.11;
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      // 2) Band-pass filter to give it a "wind-through-leaves" character
      const bandpass = ctx.createBiquadFilter();
      bandpass.type = "bandpass";
      bandpass.Q.value = 0.7;
      // Sweep center frequency: starts low (whoosh in) → rises (airy) → falls (settle)
      bandpass.frequency.setValueAtTime(420, now);
      bandpass.frequency.linearRampToValueAtTime(1400, now + dur * 0.35);
      bandpass.frequency.linearRampToValueAtTime(700, now + dur);

      // 3) Gentle low-pass on top to tame harshness
      const lowpass = ctx.createBiquadFilter();
      lowpass.type = "lowpass";
      lowpass.frequency.value = 3200;
      lowpass.Q.value = 0.4;

      // 4) Envelope — soft attack, longer tail
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.28, now + dur * 0.22);
      gain.gain.linearRampToValueAtTime(0.18, now + dur * 0.55);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);

      // 5) Subtle stereo widening via a delayed, slightly detuned copy
      const merger = ctx.createChannelMerger(2);
      const leftGain = ctx.createGain();
      const rightGain = ctx.createGain();
      const rightDelay = ctx.createDelay(0.05);
      rightDelay.delayTime.value = 0.018;
      leftGain.gain.value = 1;
      rightGain.gain.value = 0.9;

      source.connect(bandpass);
      bandpass.connect(lowpass);
      lowpass.connect(gain);
      gain.connect(leftGain);
      gain.connect(rightDelay);
      rightDelay.connect(rightGain);
      leftGain.connect(merger, 0, 0);
      rightGain.connect(merger, 0, 1);
      merger.connect(ctx.destination);

      source.start(now);
      source.stop(now + dur + 0.05);

      // Clean up nodes once finished
      source.onended = () => {
        try {
          source.disconnect();
          bandpass.disconnect();
          lowpass.disconnect();
          gain.disconnect();
          leftGain.disconnect();
          rightGain.disconnect();
          rightDelay.disconnect();
          merger.disconnect();
        } catch {
          /* noop */
        }
      };
    },
    [getCtx]
  );

  // Tear down the context when the component unmounts
  useEffect(() => {
    return () => {
      const ctx = ctxRef.current;
      if (ctx && ctx.state !== "closed") {
        void ctx.close();
      }
      ctxRef.current = null;
    };
  }, []);

  return play;
}